import React from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Box, Flex, Text } from '@updater/ui-uds';
import { NavItem } from './NavBar';
import { useTheme } from '@emotion/react';

interface HeaderNavProps {
  items: NavItem[];
}

const IconWrapper = styled(Flex)<{ isActive: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radii.xs};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.cta : 'transparent'};
  ${({ isActive }) =>
    isActive && 'box-shadow: inset 0 0 0 9999px rgba(255, 255, 255, 0.1);'}
`;

const NavItemButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs}px;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.xs};
  padding: ${({ theme }) => `${theme.space.xs}px ${theme.space.s}px`};
  background-color: transparent;
  ${({ isActive }) =>
    isActive && 'box-shadow: inset 0 0 0 9999px rgba(255, 255, 255, 0.1);'}
  transition: opacity 0.15s ease-in-out;

  color: ${({ isActive }) => (isActive ? 'white' : 'inherit')};

  &:hover span {
    text-decoration: underline;
  }

  &:hover ${IconWrapper} {
    box-shadow: inset 0 0 0 9999px rgba(255, 255, 255, 0.22);
  }
`;

export const HeaderNav: React.FC<HeaderNavProps> = ({ items }) => {
  const router = useRouter();
  const theme = useTheme();

  return (
    <Flex as="nav" alignItems="center" gap="xxs">
      {items.map((item) => {
        const isActive = router.pathname === item.path;
        const iconColor = isActive
          ? 'white'
          : theme.whiteLabelTheme?.colors.headerText;
        return (
          <NavItemButton
            key={item.key}
            isActive={isActive}
            onClick={item.onClick}
            data-testid={`header-nav-${item.key}`}
          >
            <IconWrapper
              isActive={isActive}
              justifyContent="center"
              alignItems="center"
            >
              {React.cloneElement(item.icon, { color: iconColor })}
            </IconWrapper>
            <Text
              variant={isActive ? 'mBold' : 'm'}
              color={
                isActive ? 'white' : theme.whiteLabelTheme?.colors.headerText
              }
              as="span"
            >
              {item.text}
            </Text>
            {item.dot && (
              <Box
                data-testid="notification-dot"
                height="6px"
                width="6px"
                backgroundColor="brand"
                borderRadius="50%"
                marginLeft="xxs"
              />
            )}
          </NavItemButton>
        );
      })}
    </Flex>
  );
};
